import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api, getApiUrl } from '../utils/apiConfig';
import './CampaignPlanningReview.css';

// Platform requirements (mirrors backend platformRequirements.js)
const REQUIRES_ADVERTISER_ID = ['dv360', 'ttd', 'youtube-dv360', 'madhive', 'amazon-dsp', 'yahoo'];
const REQUIRES_AD_ACCOUNT_ID = ['linkedin', 'facebook'];
const REQUIRES_PROFILE_ID = ['reddit'];
const REQUIRES_CUSTOMER_ID = ['google_search', 'google_display', 'google_pmax', 'google_demandgen'];

/**
 * CampaignPlanningReview - Review and edit campaign structure before launching.
 * Displays the launcher result in an interactive, editable table format.
 * Supports TTD, DV360, Facebook, and other platforms.
 */
const CampaignPlanningReview = ({ campaignId, accountId, userId, onLaunch, onBack, previousApiError }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [campaignData, setCampaignData] = useState(null);
  const [launcherResult, setLauncherResult] = useState(null);
  const [platform, setPlatform] = useState(null);
  const [editedResult, setEditedResult] = useState(null);
  const [hasEdits, setHasEdits] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [launching, setLaunching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingCell, setEditingCell] = useState(null); // { section, rowIndex, field }
  const [editValue, setEditValue] = useState('');
  const [showLaunchModal, setShowLaunchModal] = useState(false);
  const [launcherFilePath, setLauncherFilePath] = useState(null);
  const [launchAdvertiserId, setLaunchAdvertiserId] = useState('');
  const [launchAdAccountId, setLaunchAdAccountId] = useState('');
  const [launchProfileId, setLaunchProfileId] = useState('');
  const [launchCustomerId, setLaunchCustomerId] = useState('');
  const [launchChannelId, setLaunchChannelId] = useState('');
  const [channelConfigs, setChannelConfigs] = useState([]);
  const [channelConfigsLoading, setChannelConfigsLoading] = useState(false);
  const editingCellRef = useRef(null); // Ref to track current editing cell for blur race handling
  const editValueRef = useRef('');

  // Fetch planning review data
  useEffect(() => {
    fetchReviewData();
  }, [campaignId]);

  const fetchReviewData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get(
        getApiUrl(`/api/v2/campaigns/${campaignId}/planning-review`),
        { params: { accountId, userId } }
      );

      const data = response.data;
      setCampaignData(data.campaign);
      setLauncherResult(data.launcherResult);
      setEditedResult(JSON.parse(JSON.stringify(data.launcherResult))); // deep clone
      const detectedPlatform = data.platform || data.launcherResult?.platform || data.launcherResult?.Platform;
      setPlatform(detectedPlatform);
      setLauncherFilePath(data.launcherResultPath || null);

      // Auto-expand all top-level sections + summary
      const sections = getSectionsForPlatform(detectedPlatform, data.launcherResult);
      const expanded = { summary: true };
      sections.forEach(s => { expanded[s.key] = true; });
      setExpandedSections(expanded);
    } catch (err) {
      console.error('Error fetching planning review:', err);
      setError(err.response?.data?.error || 'Failed to load planning review data');
    } finally {
      setLoading(false);
    }
  };

  // Save edits
  const handleSaveEdits = async () => {
    try {
      setSaving(true);
      await api.put(
        getApiUrl(`/api/v2/campaigns/${campaignId}/planning-review`),
        { editedLauncherResult: editedResult, accountId, userId }
      );
      setHasEdits(false);
    } catch (err) {
      setError('Failed to save edits: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  // Fetch channel configs for the platform when launch modal opens
  const fetchChannelConfigs = async (platformName) => {
    if (!platformName || !accountId) return;
    setChannelConfigsLoading(true);
    try {
      const response = await api.get(getApiUrl(`/api/users/channel-configs/${accountId}`), {
        params: { userId }
      });
      const configs = response.data.channelConfigs || {};
      const platformConfigs = configs[platformName.toLowerCase()] || [];
      setChannelConfigs(platformConfigs);
      if (platformConfigs.length === 1) {
        setLaunchChannelId(platformConfigs[0].id);
      }
    } catch (err) {
      console.error('Error fetching channel configs:', err);
      setChannelConfigs([]);
    } finally {
      setChannelConfigsLoading(false);
    }
  };

  // Determine which fields are needed for this platform
  const getPlatformLaunchFields = () => {
    const p = (platform || '').toLowerCase();
    return {
      // Planning mode now requires the advertiser ID at creation, so only prompt for it here
      // when it wasn't captured yet (e.g. campaigns created before this was required).
      needsAdvertiserId: REQUIRES_ADVERTISER_ID.includes(p) && !campaignData?.advertiser_id,
      needsAdAccountId: REQUIRES_AD_ACCOUNT_ID.includes(p),
      needsProfileId: REQUIRES_PROFILE_ID.includes(p),
      needsCustomerId: REQUIRES_CUSTOMER_ID.includes(p),
      needsChannelConfig: channelConfigs.length > 0,
    };
  };

  // Open the pre-launch modal — save edits first if needed
  const handleLaunch = async () => {
    try {
      setError(null);

      // Save edits first so the latest launcher file is available
      if (hasEdits) {
        setSaving(true);
        const saveResponse = await api.put(
          getApiUrl(`/api/v2/campaigns/${campaignId}/planning-review`),
          { editedLauncherResult: editedResult, accountId, userId }
        );
        setLauncherFilePath(saveResponse.data.editedLauncherResultPath);
        setHasEdits(false);
        setSaving(false);
      }

      // Fetch channel configs for this platform
      if (platform) {
        await fetchChannelConfigs(platform);
      }

      setShowLaunchModal(true);
    } catch (err) {
      setError('Failed to save before launch: ' + (err.response?.data?.error || err.message));
      setSaving(false);
    }
  };

  // Submit the launch with collected metadata
  const handleConfirmLaunch = async () => {
    try {
      setLaunching(true);
      setError(null);

      const fields = getPlatformLaunchFields();
      const metadata = {};

      if (fields.needsAdvertiserId && launchAdvertiserId.trim()) {
        metadata.advertiserId = launchAdvertiserId.trim();
      }
      if (fields.needsAdAccountId && launchAdAccountId.trim()) {
        metadata.adAccountId = launchAdAccountId.trim();
      }
      if (fields.needsProfileId && launchProfileId.trim()) {
        metadata.profileId = launchProfileId.trim();
      }
      if (fields.needsCustomerId && launchCustomerId.trim()) {
        metadata.customerId = launchCustomerId.trim();
      }

      // Include channel config if selected
      // Extract field_values to match the format used by normal campaign creation flow
      if (launchChannelId) {
        const selectedConfig = channelConfigs.find(c => c.id === launchChannelId);
        if (selectedConfig) {
          metadata.channelConfig = selectedConfig.field_values || selectedConfig;
          metadata.channelId = launchChannelId;
        }
      } else if (channelConfigs.length === 1) {
        metadata.channelConfig = channelConfigs[0].field_values || channelConfigs[0];
        metadata.channelId = channelConfigs[0].id;
      }

      const response = await api.post(
        getApiUrl(`/api/v2/campaigns/${campaignId}/planning-launch`),
        {
          accountId,
          userId,
          editedLauncherResultPath: launcherFilePath,
          metadata
        }
      );

      setShowLaunchModal(false);
      if (onLaunch) onLaunch(response.data);
    } catch (err) {
      setError('Failed to launch: ' + (err.response?.data?.error || err.message));
    } finally {
      setLaunching(false);
    }
  };

  // Toggle section expand
  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Toggle row expand
  const toggleRow = (key) => {
    setExpandedRows(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Start editing a cell
  const startEdit = (section, rowIndex, field, currentValue) => {
    // If there's a currently editing cell, commit it first
    const prev = editingCellRef.current;
    if (prev && (prev.section !== section || prev.rowIndex !== rowIndex || prev.field !== field)) {
      applyEdit(prev.section, prev.rowIndex, prev.field);
    }
    const cell = { section, rowIndex, field };
    editingCellRef.current = cell;
    editValueRef.current = currentValue != null ? String(currentValue) : '';
    setEditingCell(cell);
    setEditValue(editValueRef.current);
  };

  // Apply edit value to editedResult (does NOT clear editingCell)
  const applyEdit = (section, rowIndex, field) => {
    if (!editedResult) return;
    const updated = JSON.parse(JSON.stringify(editedResult));
    let value = editValueRef.current;
    if (!isNaN(value) && value.trim() !== '') {
      value = Number(value);
    }
    updateNestedValue(updated, section, rowIndex, field, value);
    setEditedResult(updated);
    setHasEdits(true);
  };

  // Commit cell edit (called on blur / Enter)
  const commitEdit = (section, rowIndex, field) => {
    // Only commit if we're still editing the same cell
    const current = editingCellRef.current;
    if (!current || current.section !== section || current.rowIndex !== rowIndex || current.field !== field) {
      return; // Already moved to another cell via startEdit, skip
    }
    applyEdit(section, rowIndex, field);
    editingCellRef.current = null;
    setEditingCell(null);
  };

  // Cancel edit
  const cancelEdit = () => {
    editingCellRef.current = null;
    setEditingCell(null);
    setEditValue('');
  };

  // Delete a row from a section (kept local until the user saves, like cell edits)
  const deleteRow = (sectionKey, rowIndex) => {
    if (!editedResult) return;

    const updated = JSON.parse(JSON.stringify(editedResult));
    const p = normalizePlatform(platform);

    // Find the array in the data that corresponds to this section
    let targetArray;
    if (p === 'ttd') {
      if (sectionKey === 'campaigns' || sectionKey === 'Campaigns') targetArray = updated.campaigns || updated.Campaigns;
      else if (sectionKey === 'adGroups' || sectionKey === 'AdGroups') targetArray = updated.adGroups || updated.AdGroups;
      else if (sectionKey === 'packages') targetArray = updated.packages;
    } else if (p === 'dv360') {
      if (sectionKey === 'insertionOrders') targetArray = updated.insertionOrders;
      else if (sectionKey === 'lineItems') targetArray = updated.lineItems;
      else if (sectionKey === 'packages') targetArray = updated.packages;
    } else if (p === 'facebook') {
      if (sectionKey === 'ad_sets') targetArray = updated.ad_sets;
      else if (sectionKey === 'targeting_profiles') targetArray = updated.targeting_profiles;
    } else {
      // Generic: try direct key
      if (Array.isArray(updated[sectionKey])) targetArray = updated[sectionKey];
    }

    if (!targetArray || rowIndex < 0 || rowIndex >= targetArray.length) return;

    const [removed] = targetArray.splice(rowIndex, 1);

    // Keep parent/child references consistent: a deleted parent must take its
    // children with it (orphaned line items / ad groups fail at launch with
    // "Insertion order not found" or a missing campaign id), and a deleted
    // child must be dropped from its parent's id list.
    if (p === 'dv360') {
      if (sectionKey === 'insertionOrders' && Array.isArray(removed?.lineItems) && Array.isArray(updated.lineItems)) {
        const childIds = new Set(removed.lineItems);
        updated.lineItems = updated.lineItems.filter(li => !childIds.has(li.lineItemId));
      } else if (sectionKey === 'lineItems' && removed?.lineItemId && Array.isArray(updated.insertionOrders)) {
        updated.insertionOrders.forEach(io => {
          if (Array.isArray(io.lineItems)) {
            io.lineItems = io.lineItems.filter(id => id !== removed.lineItemId);
          }
        });
      }
    } else if (p === 'ttd') {
      // The launcher output PascalCases top-level keys only (campaigns →
      // Campaigns, adGroups → AdGroups); nested keys stay camelCase — a
      // campaign's child id list is `adGroups` and the ad group id field is
      // `adGroupId`. Tolerate both casings at every level.
      const campaignsArr = updated.campaigns || updated.Campaigns;
      const adGroupsKey = Array.isArray(updated.adGroups) ? 'adGroups' : (Array.isArray(updated.AdGroups) ? 'AdGroups' : null);
      const adGroupId = (ag) => ag?.adGroupId ?? ag?.AdGroupId;
      const campaignChildIds = (c) => (Array.isArray(c?.adGroups) ? c.adGroups : c?.AdGroups);
      if ((sectionKey === 'campaigns' || sectionKey === 'Campaigns') && Array.isArray(campaignChildIds(removed)) && adGroupsKey) {
        const childIds = new Set(campaignChildIds(removed));
        updated[adGroupsKey] = updated[adGroupsKey].filter(ag => !childIds.has(adGroupId(ag)));
      } else if ((sectionKey === 'adGroups' || sectionKey === 'AdGroups') && adGroupId(removed) != null && Array.isArray(campaignsArr)) {
        campaignsArr.forEach(c => {
          const key = Array.isArray(c.adGroups) ? 'adGroups' : (Array.isArray(c.AdGroups) ? 'AdGroups' : null);
          if (key) {
            c[key] = c[key].filter(id => id !== adGroupId(removed));
          }
        });
      }
    }

    setEditedResult(updated);
    setHasEdits(true);
  };

  // Handle key press in edit mode
  const handleEditKeyDown = (e, section, rowIndex, field) => {
    if (e.key === 'Enter') {
      commitEdit(section, rowIndex, field);
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  };

  // Get sections based on platform
  const getSectionsForPlatform = (platform, data) => {
    if (!data) return [];
    const p = normalizePlatform(platform);

    switch (p) {
      case 'ttd':
        return getTTDSections(data);
      case 'dv360':
        return getDV360Sections(data);
      case 'facebook':
        return getFacebookSections(data);
      default:
        return getGenericSections(data);
    }
  };

  // =========================================================
  // Platform: TTD
  // =========================================================
  const getTTDSections = (data) => {
    const sections = [];

    // Support both casings: campaigns/Campaigns, adGroups/AdGroups
    const campaigns = data.campaigns || data.Campaigns || [];
    const adGroups = data.adGroups || data.AdGroups || [];
    const campaignsKey = data.Campaigns ? 'Campaigns' : 'campaigns';
    const adGroupsKey = data.AdGroups ? 'AdGroups' : 'adGroups';

    // Campaigns
    if (campaigns.length > 0) {
      sections.push({
        key: campaignsKey,
        title: 'Campaigns',
        icon: 'campaign',
        count: campaigns.length,
        columns: ['Campaign Name', 'Budget', 'Daily Budget', 'Start Date', 'End Date'],
        rows: campaigns.map((c, i) => ({
          id: `campaign-${i}`,
          cells: [
            { field: 'CampaignName', value: c.CampaignName, editable: true },
            { field: 'Budget.Amount', value: c.Budget?.Amount, editable: true, prefix: '$' },
            { field: 'DailyBudget.Amount', value: c.DailyBudget?.Amount, editable: true, prefix: '$' },
            { field: 'StartDate', value: c.StartDate, editable: true },
            { field: 'EndDate', value: c.EndDate, editable: true }
          ],
          expandable: true,
          expandContent: c
        }))
      });
    }

    // Ad Groups
    if (adGroups.length > 0) {
      sections.push({
        key: adGroupsKey,
        title: 'Ad Groups',
        icon: 'group_work',
        count: adGroups.length,
        columns: ['Ad Group Name', 'Tactic', 'Base Bid CPM', 'Max Bid CPM', 'Pacing', 'Funnel'],
        // Two ad-group spellings reach this view: the MMGY launch template
        // (adGroupName/tactic, lowercase) and the campaign-COPY template
        // (AdGroupName, no tactic — copy_ttd_campaign_launcher's curated
        // shape, which the TTD creator reads capitalized). Cell edits are
        // written back to `field`, so the field must target whichever key the
        // object actually carries — writing 'adGroupName' onto a copy template
        // would leave the pushed 'AdGroupName' stale.
        rows: adGroups.map((ag, i) => ({
          id: `adgroup-${i}`,
          cells: [
            ag.adGroupName == null && ag.AdGroupName != null
              ? { field: 'AdGroupName', value: ag.AdGroupName, editable: true }
              : { field: 'adGroupName', value: ag.adGroupName, editable: true },
            { field: 'tactic', value: ag.tactic, editable: ag.tactic != null },
            { field: 'BaseBidCPM', value: ag.BaseBidCPM, editable: true, prefix: '$' },
            { field: 'MaxBidCPM', value: ag.MaxBidCPM, editable: true, prefix: '$' },
            { field: 'PacingMode', value: ag.PacingMode, editable: ag.PacingMode != null },
            { field: 'FunnelLocation', value: ag.FunnelLocation, editable: true }
          ],
          expandable: true,
          expandContent: ag
        }))
      });
    }

    // Packages are internal data and hidden from planning review

    return sections;
  };

  // =========================================================
  // Platform: DV360
  // =========================================================
  const getDV360Sections = (data) => {
    const sections = [];

    // Campaign
    if (data.campaign) {
      const c = data.campaign;
      sections.push({
        key: 'campaign',
        title: 'Campaign',
        icon: 'campaign',
        count: 1,
        columns: ['Display Name', 'Start Date', 'End Date'],
        rows: [{
          id: 'campaign-0',
          cells: [
            { field: 'displayName', value: c.displayName, editable: true },
            { field: 'startDate', value: formatDV360Date(c.campaignFlight?.plannedDates?.startDate), editable: true },
            { field: 'endDate', value: formatDV360Date(c.campaignFlight?.plannedDates?.endDate), editable: true }
          ],
          expandable: true,
          expandContent: c
        }]
      });
    }

    // Insertion Orders — support both plural array and singular object
    const ioList = data.insertionOrders || (data.insertionOrder ? [data.insertionOrder] : []);
    if (ioList.length > 0) {
      sections.push({
        key: data.insertionOrders ? 'insertionOrders' : 'insertionOrder',
        title: 'Insertion Orders',
        icon: 'receipt_long',
        count: ioList.length,
        columns: ['Display Name', 'Pacing', 'KPI', 'Budget', 'Status'],
        rows: ioList.map((io, i) => ({
          id: `io-${i}`,
          cells: [
            { field: 'displayName', value: io.displayName, editable: true },
            { field: 'pacing.pacingType', value: formatDV360Enum(io.pacing?.pacingType) },
            { field: 'kpi', value: io.kpi ? formatDV360Enum(io.kpi.kpiType) : '-' },
            { field: 'budget', value: formatDV360Budget(io.budget) },
            { field: 'entityStatus', value: formatDV360Enum(io.entityStatus), editable: true }
          ],
          expandable: true,
          expandContent: io
        }))
      });
    }

    // Line Items — support both plural array and singular object
    const liList = data.lineItems || (data.lineItem ? [data.lineItem] : []);
    if (liList.length > 0) {
      sections.push({
        key: data.lineItems ? 'lineItems' : 'lineItem',
        title: 'Line Items',
        icon: 'list_alt',
        count: liList.length,
        columns: ['Display Name', 'Type', 'Bid Strategy', 'Pacing', 'Status'],
        rows: liList.map((li, i) => ({
          id: `li-${i}`,
          cells: [
            { field: 'displayName', value: li.displayName, editable: true },
            { field: 'lineItemType', value: formatDV360Enum(li.lineItemType) },
            { field: 'bidStrategy', value: formatDV360BidStrategy(li.bidStrategy) },
            { field: 'pacing', value: formatDV360Enum(li.pacing?.pacingType) },
            { field: 'entityStatus', value: formatDV360Enum(li.entityStatus), editable: true }
          ],
          expandable: true,
          expandContent: li
        }))
      });
    }

    // Ad Groups — support both plural array and singular object
    const agList = data.adGroups || (data.adGroup ? [data.adGroup] : []);
    if (agList.length > 0) {
      sections.push({
        key: data.adGroups ? 'adGroups' : 'adGroup',
        title: 'Ad Groups',
        icon: 'group_work',
        count: agList.length,
        columns: ['Display Name', 'Format', 'Bid Strategy', 'Status'],
        rows: agList.map((ag, i) => ({
          id: `ag-${i}`,
          cells: [
            { field: 'displayName', value: ag.displayName, editable: true },
            { field: 'adGroupFormat', value: formatDV360Enum(ag.adGroupFormat) },
            { field: 'bidStrategy', value: formatDV360BidStrategy(ag.bidStrategy) },
            { field: 'entityStatus', value: formatDV360Enum(ag.entityStatus), editable: true }
          ],
          expandable: true,
          expandContent: ag
        }))
      });
    }

    // Packages are internal data and hidden from planning review

    // Geo Targeting
    if (data.geoTargeting) {
      const gt = data.geoTargeting;
      const geoSummary = [];
      if (gt.includedCountries?.length) geoSummary.push(`Countries: ${gt.includedCountries.join(', ')}`);
      if (gt.zipCodes?.length) geoSummary.push(`${gt.zipCodes.length} zip codes`);
      sections.push({
        key: 'geoTargeting',
        title: 'Geo Targeting',
        icon: 'location_on',
        count: 1,
        columns: ['Summary'],
        rows: [{
          id: 'geo-0',
          cells: [{ field: 'summary', value: geoSummary.join(', ') || '-' }],
          expandable: true,
          expandContent: gt
        }]
      });
    }

    return sections;
  };

  // =========================================================
  // Platform: Facebook
  // =========================================================
  const getFacebookSections = (data) => {
    const sections = [];

    // Campaign
    if (data.campaign) {
      const c = data.campaign;
      sections.push({
        key: 'campaign',
        title: 'Campaign',
        icon: 'campaign',
        count: 1,
        columns: ['Campaign Name', 'Goal', 'Budget', 'Start Date', 'End Date', 'Attribution'],
        rows: [{
          id: 'campaign-0',
          cells: [
            { field: 'campaign_name', value: c.campaign_name, editable: true },
            { field: 'goal', value: c.goal, editable: true },
            { field: 'budget', value: c.budget, editable: true, prefix: '$' },
            { field: 'start_date', value: c.start_date, editable: true },
            { field: 'end_date', value: c.end_date, editable: true },
            { field: 'attribution_setting', value: c.attribution_setting }
          ]
        }]
      });
    }

    // Ad Sets
    if (data.ad_sets?.length > 0) {
      sections.push({
        key: 'ad_sets',
        title: 'Ad Sets',
        icon: 'view_list',
        count: data.ad_sets.length,
        columns: ['Package Name', 'Tactic', 'Budget', 'Bid Strategy', 'Optimization', 'Start', 'End', 'Status'],
        rows: data.ad_sets.map((adSet, i) => ({
          id: `adset-${i}`,
          cells: [
            { field: 'package_name', value: adSet.package_name, editable: true },
            { field: 'tactic', value: adSet.tactic },
            { field: 'budget', value: adSet.budget, editable: true, prefix: '$' },
            { field: 'bid_strategy', value: adSet.bid_strategy, editable: true },
            { field: 'optimization_goal', value: adSet.optimization_goal },
            { field: 'start_date', value: adSet.start_date, editable: true },
            { field: 'end_date', value: adSet.end_date, editable: true },
            { field: 'status', value: adSet.status, editable: true }
          ],
          expandable: true,
          expandContent: adSet
        }))
      });
    }

    // Targeting Profiles
    if (data.targeting_profiles?.length > 0) {
      sections.push({
        key: 'targeting_profiles',
        title: 'Targeting Profiles',
        icon: 'target',
        count: data.targeting_profiles.length,
        columns: ['Name', 'Age Range', 'Genders', 'Geo Locations', 'Positions', 'Advantage Audience'],
        rows: data.targeting_profiles.map((tp, i) => ({
          id: `tp-${i}`,
          cells: [
            { field: 'name', value: tp.name },
            { field: 'age_range', value: `${tp.age_min || 18}-${tp.age_max || 65}` },
            { field: 'genders', value: formatGenders(tp.genders) },
            { field: 'geo_locations', value: formatGeoLocations(tp.geo_locations) },
            { field: 'positions', value: formatPositions(tp) },
            { field: 'advantage_audience', value: tp.targeting_automation?.advantage_audience ? 'On' : 'Off' }
          ],
          expandable: true,
          expandContent: tp
        }))
      });
    }

    return sections;
  };

  // =========================================================
  // Generic fallback for other platforms
  // =========================================================
  const getGenericSections = (data) => {
    const sections = [];
    const hiddenKeys = ['judge_result', 'judgeResult', 'judge', '_mmgy_metadata', 'platform', 'Platform', 'raw_gemini_response', 'creative', 'demographics', 'audienceInterests', 'creatives', 'Creatives', 'assets', 'Assets', 'partner', 'targetProfiles', 'campaignCount', 'adGroupCount', 'trackingSection', 'geographies', 'StandardTargetProfiles', 'packages'];
    const hiddenPatterns = ['judge', 'mmgy_metadata', 'raw_gemini', 'raw_gpt', 'raw_claude'];
    Object.entries(data).forEach(([key, value]) => {
      if (hiddenKeys.includes(key)) return;
      const keyLower = key.toLowerCase();
      if (hiddenPatterns.some(p => keyLower.includes(p))) return;
      if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
        const columns = Object.keys(value[0]).filter(k => typeof value[0][k] !== 'object').slice(0, 8);
        sections.push({
          key,
          title: key.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim(),
          icon: 'table_rows',
          count: value.length,
          columns: columns.map(c => c.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim()),
          rows: value.map((item, i) => ({
            id: `${key}-${i}`,
            cells: columns.map(col => ({
              field: col,
              value: item[col],
              editable: true
            })),
            expandable: true,
            expandContent: item
          }))
        });
      } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        const fields = Object.entries(value).filter(([_, v]) => typeof v !== 'object');
        if (fields.length > 0) {
          sections.push({
            key,
            title: key.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim(),
            icon: 'info',
            count: 1,
            columns: fields.map(([k]) => k.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim()),
            rows: [{
              id: `${key}-0`,
              cells: fields.map(([k, v]) => ({
                field: k,
                value: v,
                editable: true
              }))
            }]
          });
        }
      }
    });
    return sections;
  };

  // =========================================================
  // Helpers
  // =========================================================
  const normalizePlatform = (p) => {
    if (!p) return 'unknown';
    const lower = p.toLowerCase();
    if (lower.includes('ttd') || lower.includes('trade')) return 'ttd';
    if (lower.includes('dv360') || lower.includes('display')) return 'dv360';
    if (lower.includes('fb') || lower.includes('facebook') || lower.includes('meta')) return 'facebook';
    return lower;
  };

  const formatDV360Enum = (val) => {
    if (!val) return '-';
    return val.replace(/^[A-Z_]+_TYPE_/, '').replace(/_/g, ' ').toLowerCase()
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  const formatDV360Date = (dateObj) => {
    if (!dateObj) return '-';
    return `${dateObj.month}/${dateObj.day}/${dateObj.year}`;
  };

  const formatDV360BidStrategy = (bs) => {
    if (!bs) return '-';
    if (bs.fixedBid?.bidAmountMicros) return `Fixed: $${(Number(bs.fixedBid.bidAmountMicros) / 1000000).toFixed(2)}`;
    if (bs.youtubeAndPartnersBid) {
      const type = formatDV360Enum(bs.youtubeAndPartnersBid.type);
      const val = bs.youtubeAndPartnersBid.value ? ` $${(Number(bs.youtubeAndPartnersBid.value) / 1000000).toFixed(2)}` : '';
      return `${type}${val}`;
    }
    return '-';
  };

  const formatDV360Budget = (budget) => {
    const seg = budget?.budgetSegments?.[0];
    if (!seg) return '-';
    // An impression-goal IO keeps its COUNT in budgetAmountMicros, un-scaled,
    // and says so through budgetUnit. The currency branch used to win that race
    // and print a 212,717-impression goal as "$0.21".
    if (String(budget.budgetUnit || '').toUpperCase().includes('IMPRESSION')) {
      const n = Number(seg.budgetAmountInImpressions ?? seg.budgetAmountMicros ?? 0);
      return n > 0 ? `${n.toLocaleString()} imps` : '-';
    }
    if (seg.budgetAmountMicros) {
      return `$${(Number(seg.budgetAmountMicros) / 1000000).toFixed(2)}`;
    }
    // No unit declared: a template still names its impressions explicitly.
    if (seg.budgetAmountInImpressions) {
      return `${Number(seg.budgetAmountInImpressions).toLocaleString()} imps`;
    }
    return '-';
  };

  const formatGenders = (genders) => {
    if (!genders || genders.length === 0) return 'All';
    return genders.map(g => g === 1 ? 'Male' : g === 2 ? 'Female' : 'All').join(', ');
  };

  const formatGeoLocations = (geo) => {
    if (!geo) return '-';
    const parts = [];
    if (geo.country_groups?.length) parts.push(geo.country_groups.join(', '));
    if (geo.regions?.length) parts.push(`${geo.regions.length} regions`);
    if (geo.cities?.length) parts.push(`${geo.cities.length} cities`);
    if (geo.dmacodes?.length) parts.push(`${geo.dmacodes.length} DMAs`);
    if (geo.zipcodes?.length) parts.push(`${geo.zipcodes.length} zips`);
    return parts.join(', ') || '-';
  };

  const formatPositions = (tp) => {
    const parts = [];
    if (tp.facebook_positions?.length) parts.push(`FB: ${tp.facebook_positions.join(', ')}`);
    if (tp.publisher_platforms?.length) parts.push(tp.publisher_platforms.join(', '));
    return parts.join(' | ') || 'Automatic';
  };

  // Update a nested value in the edited result
  const updateNestedValue = (obj, section, rowIndex, field, value) => {
    const p = normalizePlatform(platform);

    // Handle dot-notation fields (e.g., 'Budget.Amount')
    const fieldParts = field.split('.');

    let target;
    if (p === 'ttd') {
      if (section === 'campaigns' || section === 'Campaigns') target = (obj.campaigns || obj.Campaigns)?.[rowIndex];
      else if (section === 'adGroups' || section === 'AdGroups') target = (obj.adGroups || obj.AdGroups)?.[rowIndex];
      else if (section === 'packages') target = obj.packages?.[rowIndex];
    } else if (p === 'dv360') {
      if (section === 'campaign') target = obj.campaign;
      else if (section === 'insertionOrders') target = obj.insertionOrders?.[rowIndex];
      else if (section === 'insertionOrder') target = obj.insertionOrder;
      else if (section === 'lineItems') target = obj.lineItems?.[rowIndex];
      else if (section === 'lineItem') target = obj.lineItem;
      else if (section === 'adGroups') target = obj.adGroups?.[rowIndex];
      else if (section === 'adGroup') target = obj.adGroup;
      else if (section === 'geoTargeting') target = obj.geoTargeting;
      else if (section === 'creative') target = obj.creative;
      else if (section === 'demographics') target = obj.demographics;
      else if (section === 'packages') target = obj.packages?.[rowIndex];
    } else if (p === 'facebook') {
      if (section === 'campaign') target = obj.campaign;
      else if (section === 'ad_sets') target = obj.ad_sets?.[rowIndex];
      else if (section === 'targeting_profiles') target = obj.targeting_profiles?.[rowIndex];
    }

    // Generic fallback: try direct key lookup
    if (!target) {
      if (Array.isArray(obj[section])) {
        target = obj[section]?.[rowIndex];
      } else if (typeof obj[section] === 'object' && obj[section] !== null) {
        target = obj[section];
      }
    }

    if (!target) return;

    if (fieldParts.length === 1) {
      target[field] = value;
    } else {
      let current = target;
      for (let i = 0; i < fieldParts.length - 1; i++) {
        if (!current[fieldParts[i]]) current[fieldParts[i]] = {};
        current = current[fieldParts[i]];
      }
      current[fieldParts[fieldParts.length - 1]] = value;
    }
  };

  // Render a human-readable detail panel for expanded rows with editable fields
  const renderExpandedContent = (content, sectionKey, rowIndex) => {
    if (!content || typeof content !== 'object') return null;

    // Flatten nested objects into readable key-value pairs with their field paths
    const expandHiddenPatterns = ['judge', 'mmgy_metadata', 'raw_gemini', 'raw_gpt', 'raw_claude'];
    const flattenObject = (obj, prefix = '', pathPrefix = '') => {
      const pairs = [];
      for (const [key, value] of Object.entries(obj)) {
        const keyLower = key.toLowerCase();
        if (expandHiddenPatterns.some(p => keyLower.includes(p))) continue;
        const label = prefix ? `${prefix} > ${formatFieldLabel(key)}` : formatFieldLabel(key);
        const fieldPath = pathPrefix ? `${pathPrefix}.${key}` : key;
        if (value === null || value === undefined) {
          pairs.push({ label, value: '', fieldPath, editable: true, type: 'string' });
          continue;
        }
        if (Array.isArray(value)) {
          if (value.length === 0) continue;
          if (typeof value[0] === 'object') {
            value.forEach((item, idx) => {
              pairs.push(...flattenObject(item, `${label} [${idx + 1}]`, `${fieldPath}.${idx}`));
            });
          } else {
            pairs.push({ label, value: value.join(', '), fieldPath, editable: true, type: 'list' });
          }
        } else if (typeof value === 'object') {
          pairs.push(...flattenObject(value, label, fieldPath));
        } else {
          pairs.push({ label, value: String(value), fieldPath, editable: true, type: typeof value });
        }
      }
      return pairs;
    };

    const pairs = flattenObject(content);

    // Save a single field back into editedResult
    const saveField = (fieldPath, newValue) => {
      if (!editedResult) return;
      const updated = JSON.parse(JSON.stringify(editedResult));
      let value = newValue;
      if (value !== '' && !isNaN(value) && value.trim() !== '') {
        value = Number(value);
      }
      updateNestedValue(updated, sectionKey, rowIndex, fieldPath, value);
      setEditedResult(updated);
      setHasEdits(true);
    };

    return (
      <div className="planning-expanded-content">
        <div className="planning-detail-grid">
          {pairs.map((pair, i) => (
            <div key={i} className="planning-detail-item">
              <span className="planning-detail-label">{pair.label}</span>
              {pair.editable ? (
                <input
                  type="text"
                  className="planning-detail-input"
                  defaultValue={pair.value || ''}
                  onBlur={(e) => {
                    if (e.target.value !== (pair.value || '')) {
                      saveField(pair.fieldPath, e.target.value);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.target.blur();
                  }}
                />
              ) : (
                <span className="planning-detail-value planning-detail-muted">
                  {pair.value || '-'}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Format a camelCase/snake_case field name into a readable label
  const formatFieldLabel = (key) => {
    return key
      .replace(/([A-Z])/g, ' $1')       // camelCase → camel Case
      .replace(/_/g, ' ')               // snake_case → snake case
      .replace(/\b\w/g, c => c.toUpperCase()) // capitalize words
      .trim();
  };

  // Build a high-level campaign summary from the launcher result
  const buildSummary = (data) => {
    if (!data) return [];
    const p = normalizePlatform(platform);
    const items = [];

    if (p === 'ttd') {
      const campaigns = data.campaigns || data.Campaigns || [];
      const adGroups = data.adGroups || data.AdGroups || [];
      const campaign = campaigns[0];
      if (campaign) {
        items.push({ label: 'Campaign', value: campaign.CampaignName });
        if (campaign.Budget?.Amount) items.push({ label: 'Total Budget', value: `$${Number(campaign.Budget.Amount).toLocaleString()}` });
        items.push({ label: 'Flight', value: `${campaign.StartDate || '-'} to ${campaign.EndDate || '-'}` });
      }
      if (campaigns.length > 1) items.push({ label: 'Total Campaigns', value: campaigns.length });
      if (adGroups.length) items.push({ label: 'Ad Groups', value: adGroups.length });
      const totalBudget = campaigns.reduce((sum, c) => sum + (c.Budget?.Amount || 0), 0);
      if (totalBudget && campaigns.length > 1) items.push({ label: 'Combined Budget', value: `$${totalBudget.toLocaleString()}` });
    } else if (p === 'dv360') {
      const c = data.campaign;
      if (c) {
        items.push({ label: 'Campaign', value: c.displayName });
        items.push({ label: 'Goal', value: formatDV360Enum(c.campaignGoal?.campaignGoalType) });
        items.push({ label: 'Flight', value: `${formatDV360Date(c.campaignFlight?.plannedDates?.startDate)} to ${formatDV360Date(c.campaignFlight?.plannedDates?.endDate)}` });
      }
      const ioCount = data.insertionOrders?.length || (data.insertionOrder ? 1 : 0);
      const liCount = data.lineItems?.length || (data.lineItem ? 1 : 0);
      if (ioCount) items.push({ label: 'Insertion Orders', value: ioCount });
      if (liCount) items.push({ label: 'Line Items', value: liCount });
      if (data.assets?.length) items.push({ label: 'Creative Assets', value: data.assets.length });
    } else if (p === 'facebook') {
      const c = data.campaign;
      if (c) {
        items.push({ label: 'Campaign', value: c.campaign_name });
        items.push({ label: 'Goal', value: c.goal });
        if (c.budget) items.push({ label: 'Budget', value: `$${Number(c.budget).toLocaleString()}` });
        items.push({ label: 'Flight', value: `${c.start_date || '-'} to ${c.end_date || '-'}` });
        if (c.click_url) items.push({ label: 'Landing Page', value: c.click_url, truncate: true });
      }
      if (data.ad_sets?.length) items.push({ label: 'Ad Sets', value: data.ad_sets.length });
      if (data.targeting_profiles?.length) items.push({ label: 'Targeting Profiles', value: data.targeting_profiles.length });
    } else {
      // Generic summary: pull top-level string/number fields + array counts
      for (const [key, value] of Object.entries(data)) {
        if (typeof value === 'string' || typeof value === 'number') {
          items.push({ label: formatFieldLabel(key), value: String(value) });
        } else if (Array.isArray(value)) {
          items.push({ label: formatFieldLabel(key), value: `${value.length} items` });
        }
      }
    }

    return items;
  };

  // =========================================================
  // Render
  // =========================================================
  if (loading) {
    return (
      <div className="planning-review-container">
        <div className="planning-loading">
          <div className="planning-spinner"></div>
          <p>Loading campaign structure...</p>
        </div>
      </div>
    );
  }

  if (error && !launcherResult) {
    return (
      <div className="planning-review-container">
        <div className="planning-error">
          <span className="material-symbols-outlined">error</span>
          <p>{error}</p>
          <button onClick={fetchReviewData} className="planning-btn planning-btn-secondary">Retry</button>
        </div>
      </div>
    );
  }

  const sections = getSectionsForPlatform(platform, editedResult || launcherResult);
  const platformDisplay = normalizePlatform(platform)?.toUpperCase() || 'Unknown';

  return (
    <div className="planning-review-container">
      {/* Header */}
      <div className="planning-header">
        <div className="planning-header-left">
          <div>
            <h2 className="planning-title">Campaign Planning Review</h2>
            <p className="planning-subtitle">
              <span className={`planning-platform-badge planning-platform-${normalizePlatform(platform)}`}>
                {platformDisplay}
              </span>
              {campaignData?.name}
            </p>
          </div>
        </div>
        <div className="planning-header-actions">
          {hasEdits && (
            <button
              onClick={handleSaveEdits}
              className="planning-btn planning-btn-secondary"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Draft'}
            </button>
          )}
          <button
            onClick={handleLaunch}
            className="planning-btn planning-btn-primary"
            disabled={saving}
          >
            <span className="material-symbols-outlined">rocket_launch</span>
            Launch Campaign
          </button>
        </div>
      </div>

      {previousApiError && !error && (
        <div className="planning-error-banner">
          <span className="material-symbols-outlined">warning</span>
          Previous launch failed: {previousApiError}. Please check your settings and try again.
          </div>
      )}

      {error && (
        <div className="planning-error-banner">
          <span className="material-symbols-outlined">warning</span>
          {error}
          <button onClick={() => setError(null)} className="planning-error-dismiss">&times;</button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="planning-summary-cards">
        {sections.map(section => (
          <div key={section.key} className="planning-summary-card">
            <span className="material-symbols-outlined">{section.icon}</span>
            <div>
              <div className="planning-summary-count">{section.count}</div>
              <div className="planning-summary-label">{section.title}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Sections */}
      {sections.map(section => (
        <div key={section.key} className="planning-section">
          <div
            className="planning-section-header"
            onClick={() => toggleSection(section.key)}
          >
            <div className="planning-section-title">
              <span className="material-symbols-outlined">{section.icon}</span>
              <h3>{section.title}</h3>
              <span className="planning-section-count">{section.count}</span>
            </div>
            <span className={`material-symbols-outlined planning-chevron ${expandedSections[section.key] ? 'expanded' : ''}`}>
              expand_more
            </span>
          </div>

          {expandedSections[section.key] && (
            <div className="planning-section-content">
              <div className="planning-table-wrapper">
                <table className="planning-table">
                  <thead>
                    <tr>
                      <th className="planning-th-expand"></th>
                      {section.columns.map((col, ci) => (
                        <th key={ci}>{col}</th>
                      ))}
                      {section.rows.length > 1 && <th className="planning-td-delete"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {section.rows.map((row, ri) => (
                      <React.Fragment key={row.id}>
                        <tr className={expandedRows[row.id] ? 'row-expanded' : ''}>
                          <td className="planning-td-expand">
                            {row.expandable && (
                              <button
                                onClick={() => toggleRow(row.id)}
                                className="planning-expand-btn"
                              >
                                <span className={`material-symbols-outlined ${expandedRows[row.id] ? 'expanded' : ''}`}>
                                  chevron_right
                                </span>
                              </button>
                            )}
                          </td>
                          {row.cells.map((cell, ci) => (
                            <td
                              key={ci}
                              className={cell.editable ? 'planning-cell-editable' : ''}
                              onClick={() => cell.editable && startEdit(section.key, ri, cell.field, cell.value)}
                            >
                              {editingCell &&
                                editingCell.section === section.key &&
                                editingCell.rowIndex === ri &&
                                editingCell.field === cell.field ? (
                                <input
                                  type="text"
                                  className="planning-cell-input"
                                  value={editValue}
                                  onChange={(e) => { setEditValue(e.target.value); editValueRef.current = e.target.value; }}
                                  onBlur={() => commitEdit(section.key, ri, cell.field)}
                                  onKeyDown={(e) => handleEditKeyDown(e, section.key, ri, cell.field)}
                                  ref={el => { if (el) requestAnimationFrame(() => el.focus({ preventScroll: true })); }}
                                />
                              ) : (
                                <span className="planning-cell-value">
                                  {cell.prefix && cell.value != null ? cell.prefix : ''}
                                  {cell.value != null ? String(cell.value) : '-'}
                                  {cell.editable && <span className="planning-edit-icon material-symbols-outlined">edit</span>}
                                </span>
                              )}
                            </td>
                          ))}
                          {section.rows.length > 1 && (
                            <td className="planning-td-delete">
                              <button
                                onClick={() => deleteRow(section.key, ri)}
                                className="planning-delete-btn"
                                title="Delete row"
                              >
                                <span className="material-symbols-outlined">delete</span>
                              </button>
                            </td>
                          )}
                        </tr>
                        {row.expandable && expandedRows[row.id] && (
                          <tr className="planning-detail-row">
                            <td colSpan={section.columns.length + (section.rows.length > 1 ? 2 : 1)}>
                              {renderExpandedContent(row.expandContent, section.key, ri)}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Campaign Summary */}
      {(() => {
        const summaryItems = buildSummary(editedResult || launcherResult);
        if (summaryItems.length === 0) return null;
        return (
          <div className="planning-section planning-summary-section">
            <div className="planning-section-header" onClick={() => toggleSection('summary')}>
              <div className="planning-section-title">
                <span className="material-symbols-outlined">summarize</span>
                <h3>Campaign Summary</h3>
              </div>
              <span className={`material-symbols-outlined planning-chevron ${expandedSections.summary !== false ? 'expanded' : ''}`}>
                expand_more
              </span>
            </div>
            {expandedSections.summary !== false && (
              <div className="planning-section-content">
                <div className="planning-kv-grid">
                  {summaryItems.map((item, i) => (
                    <div key={i} className="planning-kv-item">
                      <div className="planning-kv-label">{item.label}</div>
                      <div className={`planning-kv-value ${item.truncate ? 'planning-kv-truncate' : ''}`}>
                        {item.value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Footer actions */}
      <div className="planning-footer">
        <div className="planning-footer-right">
          {hasEdits && (
            <span className="planning-edits-badge">Unsaved changes</span>
          )}
          <button
            onClick={handleLaunch}
            className="planning-btn planning-btn-primary planning-btn-lg"
            disabled={saving}
          >
            Launch Campaign
          </button>
        </div>
      </div>

      {/* Pre-Launch Modal */}
      {showLaunchModal && (
        <div className="planning-launch-overlay" onClick={() => !launching && setShowLaunchModal(false)}>
          <div className="planning-launch-modal" onClick={e => e.stopPropagation()}>
            <div className="planning-launch-header">
              <h3>Launch to {platform ? platform.toUpperCase() : 'Platform'}</h3>
              <button className="planning-launch-close" onClick={() => !launching && setShowLaunchModal(false)}>
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="planning-launch-body">
              {(() => {
                const fields = getPlatformLaunchFields();
                const hasRequiredFields = fields.needsAdvertiserId || fields.needsAdAccountId || fields.needsProfileId || fields.needsCustomerId || fields.needsChannelConfig;
                return (
                  <>
                    {!hasRequiredFields && (
                      <p className="planning-launch-ready">Ready to launch this campaign to {platform ? platform.toUpperCase() : 'the platform'}.</p>
                    )}
                    {fields.needsChannelConfig && (
                      <div className="planning-launch-field">
                        <label>Channel Configuration *</label>
                        {channelConfigsLoading ? (
                          <p className="planning-launch-loading">Loading configurations...</p>
                        ) : (
                          <select
                            className="planning-launch-input"
                            value={launchChannelId}
                            onChange={e => setLaunchChannelId(e.target.value)}
                          >
                            <option value="">Select a channel config...</option>
                            {channelConfigs.map(ch => (
                              <option key={ch.id} value={ch.id}>{ch.name || ch.id}</option>
                            ))}
                          </select>
                        )}
                      </div>
                    )}
                    {fields.needsAdvertiserId && (
                      <div className="planning-launch-field">
                        <label>Advertiser ID *</label>
                        <input
                          type="text"
                          className="planning-launch-input"
                          placeholder="Enter advertiser ID"
                          value={launchAdvertiserId}
                          onChange={e => setLaunchAdvertiserId(e.target.value)}
                          autoFocus
                        />
                      </div>
                    )}
                    {fields.needsAdAccountId && (
                      <div className="planning-launch-field">
                        <label>Ad Account ID *</label>
                        <input
                          type="text"
                          className="planning-launch-input"
                          placeholder="Enter ad account ID"
                          value={launchAdAccountId}
                          onChange={e => setLaunchAdAccountId(e.target.value)}
                          autoFocus
                        />
                      </div>
                    )}
                    {fields.needsProfileId && (
                      <div className="planning-launch-field">
                        <label>Profile ID *</label>
                        <input
                          type="text"
                          className="planning-launch-input"
                          placeholder="Enter profile ID"
                          value={launchProfileId}
                          onChange={e => setLaunchProfileId(e.target.value)}
                          autoFocus
                        />
                      </div>
                    )}
                    {fields.needsCustomerId && (
                      <div className="planning-launch-field">
                        <label>Customer ID *</label>
                        <input
                          type="text"
                          className="planning-launch-input"
                          placeholder="Enter customer ID"
                          value={launchCustomerId}
                          onChange={e => setLaunchCustomerId(e.target.value)}
                          autoFocus
                        />
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
            <div className="planning-launch-footer">
              <button
                className="planning-btn planning-btn-secondary"
                onClick={() => setShowLaunchModal(false)}
                disabled={launching}
              >
                Cancel
              </button>
              <button
                className="planning-btn planning-btn-primary"
                onClick={handleConfirmLaunch}
                disabled={launching || (
                  (getPlatformLaunchFields().needsAdvertiserId && !launchAdvertiserId.trim()) ||
                  (getPlatformLaunchFields().needsAdAccountId && !launchAdAccountId.trim()) ||
                  (getPlatformLaunchFields().needsProfileId && !launchProfileId.trim()) ||
                  (getPlatformLaunchFields().needsCustomerId && !launchCustomerId.trim()) ||
                  (getPlatformLaunchFields().needsChannelConfig && !launchChannelId)
                )}
              >
                {launching && <div className="planning-spinner-small" />}
                {launching ? 'Launching...' : 'Launch Campaign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignPlanningReview;
